import {fromEvent, Observable, of, switchMap} from 'rxjs';
import {finalize, take} from "rxjs/operators";
import Hls from 'hls.js';

export class GenericElm {
  component;
  fontStyle;
  label;
  labelGrid;
  labelAnchor;
  aspectRatio;
  playing = false;
  img;
  image;
  gif;
  gifPath;
  video;
  hls;
  svg;
  emptyIcon;
  iframe;

  constructor(component) {
    this.component = component;
  }

  setTextStyle() {
    this.labelAnchor.style.fontSize = this.fontStyle.fontSize + 'px';
    this.labelAnchor.style.color = this.fontStyle.fill;
    this.labelAnchor.style.fontFamily = this.fontStyle.fontFamily;
    this.labelAnchor.style.textDecoration = this.fontStyle.textDecoration;
    this.labelAnchor.style.textAlign = this.fontStyle.align;    // hoz
    this.labelAnchor.style.alignSelf = (this.fontStyle.alignSelf!=null) ? this.fontStyle.alignSelf : "center"; // ver
    this.labelAnchor.style.whiteSpace = this.fontStyle.wrap ? "normal" : "nowrap";

    //??? this.component.nativeElement.style.pointerEvents = "none"; // prevent the keyboard in android. cause the keyboard resize the windows and that restart the presentation.
  }

  setText(label, animate=false, editable = false): void {
    this.label = String(label);
    if (this.component != null) {
      if (this.labelGrid==null) {
        this.labelGrid = document.createElement("div");
        this.labelGrid.style.display = 'grid';
        this.labelGrid.style.width = '100%';
        this.labelGrid.style.height = '100%';
        this.component.nativeElement.appendChild(this.labelGrid);
        this.labelAnchor = document.createElement("div");
        this.labelAnchor.style.overflow = 'hidden';
        this.labelGrid.appendChild(this.labelAnchor);
        if (editable) {
          this.labelAnchor.contentEditable = 'true';
          this.labelAnchor.style.margin = '0px';
          this.labelAnchor.style.background = 'none';
          this.labelAnchor.style.outline = 'none';
          this.labelAnchor.style.border = 'none';
          this.labelAnchor.style.overflow = 'hidden';
          this.labelAnchor.style.caretColor = 'white';
          this.labelAnchor.style.userSelect = 'none';
          this.labelAnchor.style.resize = 'none';
          this.labelAnchor.style.whiteSpace = 'pre-wrap';
          this.labelAnchor.spellcheck = false;
        }
      }
      this.setTextStyle();
      if (animate==false) {
        this.labelAnchor.innerText = label;
      }
    }
  }

  setEmptyIcon(renderer) {
    this.emptyIcon = document.createElement('mat-icon');
    this.emptyIcon.innerHTML = 'extension';
    this.emptyIcon.style.fontSize = '64px';
    this.emptyIcon.style.width = '80px';
    this.emptyIcon.style.height = '80px';
    this.emptyIcon.style.color = 'white'
    renderer.addClass(this.emptyIcon, 'mat-icon');
    renderer.addClass(this.emptyIcon, 'material-icons');
    this.component.nativeElement.appendChild(this.emptyIcon)
  }


  setMedia(path: string, aspectRatio: boolean, width: number, height: number, autoRewind, volume, ext: string = null): Observable<any> {
    if (ext == null) {
      if (path.indexOf('.m3u8')!=-1) {
        ext = 'm3u8';
      } else if (path.indexOf('.mp4')!=-1) {
        ext = 'mp4';
      }
    }

    if (ext=='mp4' || ext=='m3u8') {
      return this.setVideo(path, aspectRatio, width, height, autoRewind, volume, ext);
    } else {
      return this.setImage(path, aspectRatio, width, height);
    }
  }

  setImage(path: string, aspectRatio: boolean, width: number, height: number): Observable<any> {
    return new Observable<any>( sub => {
      this.clear();
      this.aspectRatio = aspectRatio;
      sub.next('setImage');
      sub.complete()
    }).pipe(
      switchMap( r1 => {
        if (this.component!=null) {  // html
          //Log.d("DIV: setImage");

          if (path.lastIndexOf('.svg') != -1 && this.svg!=null) {
            return new Observable<any>( sub => {
              fetch(path).then( response => {
                response.text().then( svg => {
                  this.component.nativeElement.innerHTML = svg;
                  const svgElm = this.component.nativeElement.firstElementChild;
                  svgElm.style.width = '100%'
                  svgElm.style.height = '100%'
                  svgElm.setAttribute("preserveAspectRatio", "none")
                  if (this.svg!=null) {
                    for (let path in this.svg) {
                      const item = this.svg[path];
                      let elm = svgElm;
                      path.split('.').forEach(index => {
                        if (elm!=null) {
                          elm = elm.children[index];
                        }
                      })
                      if (item!=null && elm!=null) {
                        if (item.fill != null) {
                          elm.style.fill = item.fill;
                        }
                        if (item.stroke != null) {
                          elm.style.stroke = item.stroke;
                        }
                      }
                    }
                  }

                  sub.next('svg')
                  sub.complete();
                })
              })
            } )
          } else {
            let url = path;
            if (url=="") {
              return of(null);
            }
            this.img = document.createElement("img");
            this.img.style.width = '100%';
            this.img.style.height = '100%';
            if (this.aspectRatio) {
              this.img.style.objectFit = 'contain';
            }
            this.component.nativeElement.appendChild(this.img);
            this.img.src = url;
            return fromEvent(this.img, 'load').pipe(
              take(1)
            )
          }
        }
      })
    )
  }


  private setVideo(path: string, aspectRatio: boolean, width: number, height: number, autoRewind, volume, ext=null): Observable<any> {
    return new Observable<any>( sub => {
      this.clear();
      this.aspectRatio = aspectRatio;
      sub.next('setVideo');
      sub.complete();
    }).pipe(
      switchMap( r1 => {
        if (this.component != null) {
          this.video = document.createElement("video");
          this.video.style.width = '100%';
          this.video.style.height = '100%';

          this.video.muted = true;
          this.video.setAttribute("controlsList", "nodownload");
          this.video.setAttribute("controls","controls")
          if (aspectRatio == false) {
            this.video.style.objectFit = "fill";
          }
          this.video.loop = autoRewind;
          this.video.volume = volume;
          this.component.nativeElement.appendChild(this.video);

          if (ext=='m3u8') {
            this.hls = new Hls();
            this.hls.loadSource(path);
            this.hls.attachMedia(this.video);
            return fromEvent(this.hls, 'hlsManifestParsed').pipe(
              take(1)
            )
          } else {
            this.video.type="video/mp4s";
            this.video.src = path;
            return of('mp4');
          }
        }
      })
    )
  }


  setHtml(url: string, scrollbars: boolean): Observable<any> {
    this.iframe = document.createElement('iframe');
    this.iframe.style.width = '100%';
    this.iframe.style.height = '100%';
    this.iframe.style.border = '0px';
    if (scrollbars==false) {
      this.iframe.scrolling = 'no';
    }
    this.component.nativeElement.appendChild(this.iframe);
    this.iframe.src = url;
    return of('html');
  }

  play(playVideoInFull: boolean):Observable<any> {
    if (this.video==null) {
      return of('play');
    }
    let obs$ =new Observable<any>( sub => {
      this.playing = true;
      this.video.currentTime = 0;
      this.video.muted = false;
      const promise = this.video.play();
      if (promise !== undefined) {
        promise.then(_ => {
          // Play works!
          sub.next('video_play');
          sub.complete();
        }).catch(error => {
          // Play was prevented.
          if (this.video!=null) {
            this.video.muted = true;
            this.video.play();
          }
          sub.next('video_play');
          sub.complete();
        });
      }
    });
    if (playVideoInFull) {
      obs$ = obs$.pipe(
        switchMap( video => {
          return fromEvent(this.video, 'ended').pipe(
            take(1)
          )
        })
      )
    }
    return obs$;
  }

  stop() {
    //const logInfo = Log.getLogInfo("GenericElm", "stop", []);
    this.playing = false;
    if (this.component!=null && this.video!=null) {
      this.video.pause();
      //Log.d(logInfo, LogTag.PLAYER, 'video stop: 5sec !!!');
      this.video.currentTime = 5;
    } else {

    }

  }

  clear() {
    if (this.component!=null) {
      if (this.emptyIcon!=null) {
        this.component.nativeElement.removeChild(this.emptyIcon);
        this.emptyIcon = null;
      }

      if (this.labelAnchor != null) {
        this.labelGrid.removeChild(this.labelAnchor);
        this.component.nativeElement.removeChild(this.labelGrid);
        this.labelGrid = null;
        this.labelAnchor = null;
      }

      if (this.img != null) {
        this.component.nativeElement.removeChild(this.img);
        this.img = null;
      }

      if (this.hls!=null) {
        this.hls.destroy();
        this.hls = null;
      }

      if (this.video!=null) {
        try {
          this.video.pause();
          this.video.removeAttribute('src');
          this.video.load();
        } catch (e) {
          debugger;
        }
        this.component.nativeElement.removeChild(this.video);
        this.video = null;
      }

      if (this.iframe != null) {
        this.component.nativeElement.removeChild(this.iframe);
        this.iframe = null;
      }
    }
  }

  destroy(): void {
    this.clear();
  }
}

